import { Injectable } from '@angular/core';
import { FullConditionsOfApplicabilityFragment } from '@lib/models/fragment/fiche-config';
import {
  ConditionsOfApplicability,
  ConfigStatut,
  ConseilConfig,
  CreateDraftConseilConfigInput,
  ReferentielConfig,
} from '@lib/models/generated/graphql';
import { gql } from 'apollo-angular';
import { QueryManagerService } from 'finzzle-angular-utils';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export enum FicheConfigType {
  Digitale,
  AvecDoc,
  SansDoc,
}

export const ConseilConfigAbstractDataFragment = gql`
  fragment ConseilConfigAbstractDataFragment on ConseilConfig {
    id
    libelle
    statut
    isMajeurVersion
    previousVersionDeprecationDate
    conditionsOfApplicability {
      ...FullConditionsOfApplicability
    }
    version
    commentaireDeVersion
    configFormulaire {
      version
    }
    configTemplate {
      version
      templateName
    }
    lastChangeDate
    validityLimitDate
    legacyDocumentsDetails {
      id
      fileName
      fileNameWithExtension
      denomination
      fileType
    }
  }
  ${FullConditionsOfApplicabilityFragment}
`;

const ALL_CONFIGs = gql`
  query allConfigs {
    allConseilConfigs {
      ...ConseilConfigAbstractDataFragment
    }

    referentielConfigsToAddAsConseilConfig {
      id
      libelle
      conditionsOfApplicability {
        ...FullConditionsOfApplicability
      }
      applicableConfiguration {
        version
        id
      }
    }
  }
  ${ConseilConfigAbstractDataFragment}
`;

const CREATE_LEGACY_DOC_CONFIG = gql`
  mutation createLegacyDocumentConfig(
    $coaId: String!
    $libelle: String!
    $commentaireDeVersion: String!
    $storageFilesIds: [String!]!
  ) {
    createNewLegacyDocumentConfig(
      coaId: $coaId
      libelle: $libelle
      commentaireDeVersion: $commentaireDeVersion
      storageFilesIds: $storageFilesIds
    ) {
      ...ConseilConfigAbstractDataFragment
    }
  }
  ${ConseilConfigAbstractDataFragment}
`;

const IS_UPDATED_IN_REFERENTIEL_CONFIG = gql`
  query isConfigFormulaireUpdatedInReferentiel($coaId: String!) {
    isConfigFormulaireUpdatedInReferentiel(coaId: $coaId)
  }
`;

const CREATE_NEW_VERSION_CONFIG = gql`
  mutation createDraftConseilConfig($input: CreateDraftConseilConfigInput!) {
    createDraftConseilConfig(input: $input) {
      ...ConseilConfigAbstractDataFragment
    }
  }
  ${ConseilConfigAbstractDataFragment}
`;

const ACTIVATE_CONFIG = gql`
  mutation activateDraftConseilConfig($conseilConfigId: String!) {
    activateDraftConseilConfig(conseilConfigId: $conseilConfigId) {
      ...ConseilConfigAbstractDataFragment
    }
  }
  ${ConseilConfigAbstractDataFragment}
`;

const DELETE_CONFIG = gql`
  mutation deleteDraftConseilConfig($conseilConfigId: String!) {
    deleteDraftConseilConfig(conseilConfigId: $conseilConfigId)
  }
`;

export type AdminConfig = {
  conditionsOfApplicability: ConditionsOfApplicability;
  conseilConfig?: ConseilConfig;
  type: FicheConfigType;
  statut: ConfigStatut;
  libelle: string;
};

export type AdminConfigList = {
  configs: AdminConfig[];
  loading: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class AdminConfigService {
  public adminConfigsSubject: BehaviorSubject<AdminConfigList> = new BehaviorSubject<AdminConfigList>({
    configs: [],
    loading: false,
  });
  constructor(private queryManager: QueryManagerService) {}

  fetchAllConfigs() {
    this.adminConfigsSubject.next({ configs: [], loading: true });
    this.queryManager
      .query<{ referentielConfigsToAddAsConseilConfig: ReferentielConfig[]; allConseilConfigs: ConseilConfig[] }>({
        query: ALL_CONFIGs,
        fetchPolicy: 'network-only',
      })
      .subscribe(result => {
        const conseilConfigs = result?.data?.allConseilConfigs;
        const referentielConfigs = result?.data?.referentielConfigsToAddAsConseilConfig;
        let adminConfigs: AdminConfig[] = conseilConfigs.map(conseilConfig => {
          let type;
          if (conseilConfig.configFormulaire) {
            type = FicheConfigType.Digitale;
          } else if (conseilConfig.legacyDocumentsDetails && conseilConfig.legacyDocumentsDetails.length > 0) {
            type = FicheConfigType.AvecDoc;
          } else {
            type = FicheConfigType.SansDoc;
          }
          return {
            conditionsOfApplicability: conseilConfig.conditionsOfApplicability,
            conseilConfig: conseilConfig,
            type,
            libelle: conseilConfig.libelle,
            statut: conseilConfig.statut,
          };
        });

        let existingCoaIds = adminConfigs.map(coa => coa.conditionsOfApplicability.id);
        let referentielConfigsToAdd = referentielConfigs.filter(
          referentielConfig =>
            !existingCoaIds.includes(referentielConfig.conditionsOfApplicability.id) &&
            !referentielConfig.applicableConfiguration
        );
        let adminConfigsToAdd = referentielConfigsToAdd.map(referentielConfig => {
          return {
            conditionsOfApplicability: referentielConfig.conditionsOfApplicability,
            type: FicheConfigType.SansDoc,
            libelle: referentielConfig.libelle,
            statut: ConfigStatut.Disabled,
          };
        });
        adminConfigs = [...adminConfigs, ...adminConfigsToAdd];
        this.adminConfigsSubject.next({ configs: adminConfigs, loading: false });
      });
  }

  async createLegacyFilesConfig(
    coaId: string,
    storageFilesIds: string[] = [],
    libelle?: string,
    commentaireDeVersion?: string
  ) {
    let result = await firstValueFrom(
      this.queryManager.mutate<{ createnewLegacyDocumentConfig: ConseilConfig }>({
        mutation: CREATE_LEGACY_DOC_CONFIG,
        variables: {
          coaId: coaId,
          libelle: libelle ?? '',
          commentaireDeVersion: commentaireDeVersion ?? '',
          storageFilesIds: storageFilesIds,
        },
      })
    );

    //reload configs after change
    this.fetchAllConfigs();
  }

  async isUpdatedInReferentielConfig(coaId: string): Promise<boolean> {
    let result = await firstValueFrom(
      this.queryManager.query<{ isConfigFormulaireUpdatedInReferentiel: boolean }>({
        query: IS_UPDATED_IN_REFERENTIEL_CONFIG,
        variables: {
          coaId: coaId,
        },
      })
    );
    return result.data.isConfigFormulaireUpdatedInReferentiel;
  }

  async createNewDraftConseilConfig(input: CreateDraftConseilConfigInput) {
    let result = await firstValueFrom(
      this.queryManager.mutate<{ createDraftConseilConfig: ConseilConfig }>({
        mutation: CREATE_NEW_VERSION_CONFIG,
        variables: {
          input: input,
        },
      })
    );
    return result.data?.createDraftConseilConfig;
  }

  async activateDraftConseilConfig(configId: string) {
    let result = await firstValueFrom(
      this.queryManager.mutate<{ activateDraftConseilConfig: ConseilConfig }>({
        mutation: ACTIVATE_CONFIG,
        variables: {
          conseilConfigId: configId,
        },
      })
    );
    return result.data?.activateDraftConseilConfig;
  }

  async deleteConseilConfig(configId: string) {
    let result = await firstValueFrom(
      this.queryManager.mutate<{ deleteDraftConseilConfig: ConseilConfig }>({
        mutation: DELETE_CONFIG,
        variables: {
          conseilConfigId: configId,
        },
      })
    );
    return result.data?.deleteDraftConseilConfig;
  }
}
